import React, { useEffect, useMemo, useState } from 'react'
import { Plus, Cloud, CheckCircle, Clock, AlertTriangle, XCircle, DollarSign, Sparkles, Globe, Server, Database, Laptop, Download } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import DataTable from '../../components/DataTable/DataTable'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import subscriptionService from '../../services/subscriptionService'
import {
  defaultSubscriptionStats,
  extractSubscriptions,
  formatCurrency,
  formatDate,
  formatRenewalFrequency,
  getCriticalityClass,
  getSubscriptionStatusClass,
  resolveInrEquivalent,
  resolveMonthlyCost,
  transformSubscriptionRow
} from './subscriptionUtils'
import { exportSubscriptionResourcesExcel } from './exportSubscriptionResources'
import '../../styles/subscriptions-tables.css'

const SubscriptionList = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const listFilter = searchParams.get('filter') || ''
  const isNewFilter = listFilter === 'new'

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(defaultSubscriptionStats())
  const [deleteItem, setDeleteItem] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  useEffect(() => {
    loadData()
    loadStats()
  }, [listFilter])

  const loadData = async () => {
    try {
      setLoading(true)
      const params = { limit: 500 }
      if (listFilter) params.filter = listFilter
      const response = await subscriptionService.getAll(params)
      if (response.success) {
        setData(extractSubscriptions(response).map(transformSubscriptionRow))
      } else {
        toast.error(response.message || 'Failed to load subscriptions')
        setData([])
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error)
      if (!error._errorHandled) toast.error('Failed to load subscriptions')
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const response = await subscriptionService.getStats()
      if (response.success && response.data) {
        setStats({ ...defaultSubscriptionStats(), ...response.data })
      }
    } catch (error) {
      console.error('Error loading subscription stats:', error)
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'subscriptionNumber',
      header: 'Subscription #',
      cell: ({ getValue }) => <span className="code-text">{getValue() || '-'}</span>
    },
    {
      accessorKey: 'subscriptionName',
      header: 'Name',
      cell: ({ getValue, row }) => (
        <div>
          <div className="description-text">{getValue() || '-'}</div>
          {(row.original.vendorName || row.original.provider) && (
            <div className="sub-name-vendor">
              {row.original.vendorName || row.original.provider}
            </div>
          )}
        </div>
      )
    },
    {
      accessorKey: 'billingCycle',
      header: 'Renewal Frequency',
      cell: ({ getValue }) => {
        const label = formatRenewalFrequency(getValue())
        if (label === '—') return '—'
        return <span className="sub-cycle">{label}</span>
      }
    },
    {
      accessorKey: 'criticality',
      header: 'Priority',
      meta: { align: 'center' },
      cell: ({ getValue }) => {
        const v = getValue()
        if (!v) return '—'
        return <span className={`sub-crit ${getCriticalityClass(v)}`}>{v}</span>
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { align: 'center' },
      cell: ({ getValue }) => (
        <span className={`sub-status ${getSubscriptionStatusClass(getValue())}`}>
          {getValue() || '-'}
        </span>
      )
    },
    {
      accessorKey: 'startDate',
      header: 'Start',
      meta: { align: 'center' },
      cell: ({ getValue }) => formatDate(getValue())
    },
    {
      accessorKey: 'renewalDate',
      header: 'Renewal',
      meta: { align: 'center' },
      cell: ({ getValue, row }) => formatDate(getValue() || row.original.expiryDate || row.original.endDate)
    },
    {
      accessorKey: 'monthlyCost',
      header: 'Monthly Cost',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const amount = resolveMonthlyCost(row.original)
        const currency = row.original.currency || 'INR'
        const inr = resolveInrEquivalent(row.original, amount)
        return (
          <div className={`sub-cost${amount ? '' : ' sub-cost--zero'}`}>
            <span className="sub-cost__main">{formatCurrency(amount, currency)}</span>
            {inr != null && (
              <span className="sub-cost__sub">≈ {formatCurrency(inr, 'INR')}</span>
            )}
          </div>
        )
      }
    }
  ], [])

  const breadcrumbItems = [
    { label: 'Subscriptions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
    { label: 'Transactions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
    {
      label: isNewFilter ? 'New Subscriptions' : 'All Subscriptions',
      isCurrent: true
    }
  ]

  const handleAdd = () => navigate('/subscriptions/new')
  const handleView = (row) => navigate(`/subscriptions/view/${row.id}`)
  const handleEdit = (row) => navigate(`/subscriptions/edit/${row.id}`)
  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const handleExportResources = async (row) => {
    try {
      setLoading(true)
      const response = await subscriptionService.getById(row.id)
      const sub = response?.data || response?.subscription
      if (!response?.success || !sub) {
        toast.error(response?.message || 'Failed to load subscription resources')
        return
      }
      const resources = sub.resources || []
      if (!resources.length) {
        toast.info('This subscription has no resources to export')
        return
      }
      const result = exportSubscriptionResourcesExcel({
        subscription: sub,
        resources,
      })
      toast.success(`Exported ${result.count} resource(s)`)
    } catch (error) {
      console.error('Export resources failed:', error)
      if (!error._errorHandled) toast.error(error.message || 'Failed to export resources')
    } finally {
      setLoading(false)
    }
  }

  const listActions = (row) => [
    { type: 'view', label: 'View Details', onClick: () => handleView(row) },
    { type: 'edit', label: 'Edit', onClick: () => handleEdit(row) },
    {
      type: 'export',
      label: 'Export Resources (Excel)',
      icon: <Download size={16} />,
      onClick: () => handleExportResources(row),
    },
    { type: 'delete', label: 'Delete', onClick: () => handleDelete(row) },
  ]

  const confirmDelete = async () => {
    if (!deleteItem) return
    try {
      setLoading(true)
      const response = await subscriptionService.remove(deleteItem.id)
      if (response.success) {
        toast.success('Subscription deleted successfully')
        loadData()
        loadStats()
      } else {
        toast.error(response.message || 'Failed to delete subscription')
      }
    } catch (error) {
      console.error('Error deleting subscription:', error)
      if (!error._errorHandled) toast.error('Failed to delete subscription')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
      setLoading(false)
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isNewFilter ? 'New Subscriptions' : 'Subscriptions'}</h1>
          <p>
            {isNewFilter
              ? 'Recently added subscriptions from the last 30 days'
              : 'Manage recurring subscriptions, billing, and renewals'}
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Subscription
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><Cloud size={16} /></div>
            <div className="metric-trend positive"><span>Total</span></div>
          </div>
          <div className="metric-value">{stats.total}</div>
          <div className="metric-title">Total Subscriptions</div>
          <div className="metric-subtitle">Active portfolio</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><CheckCircle size={16} /></div>
            <div className="metric-trend positive"><span>Active</span></div>
          </div>
          <div className="metric-value">{stats.active}</div>
          <div className="metric-title">Active</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><Clock size={16} /></div>
            <div className="metric-trend neutral"><span>Trial</span></div>
          </div>
          <div className="metric-value">{stats.trial}</div>
          <div className="metric-title">Trial</div>
          <div className="metric-subtitle">Evaluation period</div>
        </div>
        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon"><AlertTriangle size={16} /></div>
            <div className="metric-trend negative"><span>Expiring</span></div>
          </div>
          <div className="metric-value">{stats.expiring}</div>
          <div className="metric-title">Expiring Soon</div>
          <div className="metric-subtitle">Next 30 days</div>
        </div>
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><Sparkles size={16} /></div>
            <div className="metric-trend positive"><span>Recent</span></div>
          </div>
          <div className="metric-value">{stats.recent}</div>
          <div className="metric-title">New (30 days)</div>
          <div className="metric-subtitle">Recently added</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><XCircle size={16} /></div>
            <div className="metric-trend neutral"><span>Cancelled</span></div>
          </div>
          <div className="metric-value">{stats.cancelled}</div>
          <div className="metric-title">Cancelled</div>
          <div className="metric-subtitle">No longer active</div>
        </div>
        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon"><DollarSign size={16} /></div>
            <div className="metric-trend positive"><span>Monthly</span></div>
          </div>
          <div className="metric-value">{formatCurrency(stats.monthlyCost, 'INR')}</div>
          <div className="metric-title">Monthly Cost</div>
          <div className="metric-subtitle">Active subscriptions</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><Globe size={16} /></div>
            <div className="metric-trend positive"><span>Domains</span></div>
          </div>
          <div className="metric-value">{stats.domains}</div>
          <div className="metric-title">Total Domains</div>
          <div className="metric-subtitle">GoDaddy &amp; registrars</div>
        </div>
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><Server size={16} /></div>
            <div className="metric-trend positive"><span>Droplets</span></div>
          </div>
          <div className="metric-value">{stats.droplets}</div>
          <div className="metric-title">Total Droplets</div>
          <div className="metric-subtitle">DigitalOcean compute</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><Database size={16} /></div>
            <div className="metric-trend positive"><span>MongoDB</span></div>
          </div>
          <div className="metric-value">{stats.databases}</div>
          <div className="metric-title">Total MongoDB DBs</div>
          <div className="metric-subtitle">Database configurations</div>
        </div>
        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon"><Laptop size={16} /></div>
            <div className="metric-trend neutral"><span>Rentals</span></div>
          </div>
          <div className="metric-value">{stats.rentalAssets}</div>
          <div className="metric-title">Rented Laptops</div>
          <div className="metric-subtitle">IT Vision rentals</div>
        </div>
      </div>

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        customActions={listActions}
        noCard={true}
        pageSize={10}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteItem(null) }}
        onConfirm={confirmDelete}
        title="Delete Subscription"
        message={`Delete subscription "${deleteItem?.subscriptionName}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default SubscriptionList
